// Спільні дрібниці адмінки: картки, таблиці, смужка пульсу, прості графіки.
//
// Графіки намальовані руками в SVG, без бібліотеки: у нас лінія, стовпчики
// й кільце — на них amCharts важив би більше, ніж уся адмінка, а керувати
// виглядом усе одно довелося б через теми.
import { useEffect, useMemo, useRef, useState } from "react";

export const fmt = {
  uah: (n) => `${Math.round(Number(n ?? 0)).toLocaleString("uk-UA")} ₴`,
  int: (n) => Number(n ?? 0).toLocaleString("uk-UA"),
  day: (d) => new Date(d).toLocaleDateString("uk-UA", { day: "2-digit", month: "2-digit" }),
  dayFull: (d) => new Date(d).toLocaleDateString("uk-UA", { day: "2-digit", month: "2-digit", year: "numeric" }),
  time: (d) => new Date(d).toLocaleString("uk-UA", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }),
  // «181 відповідей» — не українська. Три форми, як усюди в панелі:
  // 1 відповідь, 2 відповіді, 5 відповідей (і 11 — теж «відповідей»).
  plural: (n, one, few, many) => {
    const t = Math.abs(Math.round(n)) % 100, d = t % 10;
    if (t > 10 && t < 20) return many;
    return d === 1 ? one : d >= 2 && d <= 4 ? few : many;
  },
  ago(d) {
    if (!d) return "—";
    const s = (Date.now() - new Date(d).getTime()) / 1000;
    if (s < 60) return `${Math.round(s)} с тому`;
    if (s < 3600) return `${Math.round(s / 60)} хв тому`;
    if (s < 86400) return `${Math.round(s / 3600)} год тому`;
    return `${Math.round(s / 86400)} дн тому`;
  },
};

// Метрики телеметрії приходять у jsonb і відрізняються між джерелами:
// малина шле одне, автомат шле щось своє, чого ми ще не бачили. Показуємо
// те, що прийшло, а знайомі ключі підписуємо людською мовою. Словник тут,
// а не на екрані точки, бо читають його двоє: сторінка POS і рядок на
// дашборді здоровʼя — і підпис для нового ключа має зʼявитись одразу в обох.
export const METRIC_LABELS = {
  cpu: "CPU, %", temp_c: "температура, °C", uptime_s: "аптайм", mem_used_mb: "памʼять, МБ",
  // Пінг — до api.extrovert.cafe, 5 пакетів раз на хвилину: це «чи
  // достукується малина до нашого сервера», а не швидкість інтернету
  // (перейменовано 14.10.2026).
  ping_ms: "пінг до сервера, мс", jitter_ms: "jitter пінгу, мс", loss_pct: "втрати пінгу до сервера, %",
  mem_total_mb: "памʼять всього, МБ", disk_free_mb: "диск вільно, МБ",
  release: "реліз", kiosk_fps: "fps кіоска",
  monitor_on: "монітор", video_ok: "відеопотік",
  monitor_src: "чим перевірено монітор", monitor_woke: "будили монітор",
  root_ro: "картка read-only", usb_ok: "флешка",
  // Рядки лога ядра про біду з SD, USB і памʼяттю (pitelemetry.c, 17.10.2026).
  kernel_errors: "помилок ядра з увімкнення",
  kiosk_frames: "кадрів усього",
  // Якою дорогою малина зараз в інтернеті: Wi-Fi закладу чи LTE (14.10.2026).
  uplink: "канал в інтернет", wifi_dbm: "сигнал Wi-Fi, дБм",
  // Канал угору за останньою заливкою відео (stack/uploader.sh, 12.10.2026).
  // Стеля — та, з якою йшла заливка: швидкість, що з нею зрівнялась, означає
  // «щонайменше стільки», а не «канал дає рівно стільки».
  upload_kb_s: "заливка вгору, КБ/с", upload_limit_kb_s: "стеля заливки, КБ/с",
  upload_ok: "остання заливка вдалась", upload_at: "остання заливка",
  // Черга на флешці (uploader.sh пише state/upload.queue, 14.10.2026).
  upload_queue: "сегментів у черзі на заливку", upload_oldest: "найстаріший у черзі записаний",
  // Кавомашина Jetinno (jobs/jetinno.js)
  online: "машина онлайн", session: "сесія порталу", last_login: "востаннє в мережі",
  // «Лічильник машини» в підписі навмисно: get_device повертав 0 тоді, коли
  // на машині були й відчинені дверцята, і повний піддон, — правду каже
  // список нижче, і плутати їх не можна (11.10.2026).
  faults: "несправності (лічильник машини)", warnings: "попередження (лічильник машини)",
  supply_short: "дефіцит інгредієнтів",
  fault_codes: "активні несправності", faults_recent_24h: "події за добу (скинуті)",
  orders_card_24h: "карткових замовлень за добу",
  orders_cash_24h: "готівкових замовлень за добу", receipts_24h: "чеків за добу",
  reconcile_ok: "замовлення сходяться з чеками", note: "примітка",
  // Пари замовлення↔чек, що не знайшлись (jobs/jetinno.js, 17.10.2026).
  card_no_receipt_24h: "карткових замовлень без чека", receipts_no_order_24h: "чеків без замовлення",
};

// Поріг втрат пінгу до сервера — спільний для сторінки точки й дашборда
// здоров'я. Пінгів п'ять (telemetry.sh), тож один загублений — уже 16–17 %:
// з порогом «< 5 %» усе червоніло від кожного одиночного пакета (власник,
// 14.10.2026). Тепер погано — від двох загублених (28 %).
export const LOSS_BAD_PCT = 20;

// Без назви закладу: точка не обовʼязково стоїть у клубі, а Wi-Fi — той, до
// якого її підключили там, де вона стоїть (власник, 16.10.2026).
export const UPLINK = { wifi: "Wi-Fi закладу", lte: "LTE (запасний)" };

export const metricValue = (key, value) => {
  if (key === "uplink") return UPLINK[value] ?? (value ?? "—");
  if (key === "uptime_s") {
    const h = Number(value) / 3600;
    return h < 48 ? `${h.toFixed(1)} год` : `${(h / 24).toFixed(1)} діб`;
  }
  // Час машини з порталу — як і решта часу в таблиці, а не сирий ISO.
  if (key === "last_login" || key === "upload_at" || key === "upload_oldest") return value ? fmt.time(value) : "—";
  // Списки (несправності, події за добу) — крапкою з комою, бо самі рядки
  // вже містять коми в описах; порожній список читається як «немає».
  if (Array.isArray(value)) return value.length ? value.join("; ") : "немає";
  return typeof value === "boolean" ? (value ? "так" : "ні") : String(value);
};

// Період графіків телеметрії (власник, 27.09.2026). Один на всю адмінку й
// запам'ятовується: відкрив «Здоров'я» за добу — і сторінка точки теж за
// добу, а не знову за тиждень.
export const RANGES = [["6h", "6 годин"], ["1d", "доба"], ["3d", "3 дні"], ["7d", "тиждень"], ["30d", "місяць"]];
export const rangeLabel = (key) => RANGES.find(([k]) => k === key)?.[1] ?? "тиждень";
const RANGE_KEY = "admin.range";

export function useRange() {
  const [range, setRange] = useState(() => {
    try { return RANGES.some(([k]) => k === localStorage.getItem(RANGE_KEY)) ? localStorage.getItem(RANGE_KEY) : "7d"; } catch { return "7d"; }
  });
  const pick = (next) => { setRange(next); try { localStorage.setItem(RANGE_KEY, next); } catch { /* приватний режим */ } };
  return [range, pick];
}

export const RangePicker = ({ value, onChange }) => (
  <div className="chips">
    {RANGES.map(([k, label]) => (
      <button key={k} className={`pill${value === k ? " on" : ""}`} onClick={() => onChange(k)}>{label}</button>
    ))}
  </div>
);

export const Card = ({ title, note, children, className = "", ...rest }) => (
  <section className={`card ${className}`} {...rest}>
    {(title || note) && (
      <div className="card-head">
        {title && <h2>{title}</h2>}
        {note && <span className="note">{note}</span>}
      </div>
    )}
    {children}
  </section>
);

export const Kpi = ({ label, value, note, tone }) => (
  <section className="card">
    <div className="kpi-label">{label}</div>
    <div className={`kpi-value${tone ? ` ${tone}` : ""}`}>{value}</div>
    <div className="kpi-note">{note}</div>
  </section>
);

export const Badge = ({ tone = "", children }) => <span className={`badge ${tone}`}>{children}</span>;
export const Dot = ({ ok }) => <i className={`dot ${ok === null || ok === undefined ? "" : ok ? "ok" : "bad"}`} />;
export const Empty = ({ children }) => <div className="empty">{children}</div>;

export function DateRange({ value, onChange }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const preset = (days) => {
    const to = new Date();
    const from = new Date(to.getTime() - days * 86400_000);
    onChange({ from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) });
  };
  return (
    <div className="row">
      <label className="field"><input type="date" value={value.from ?? ""} onChange={(e) => set({ from: e.target.value })} /></label>
      <label className="field"><input type="date" value={value.to ?? ""} onChange={(e) => set({ to: e.target.value })} /></label>
      <button className="btn" onClick={() => preset(7)}>7 днів</button>
      <button className="btn" onClick={() => preset(30)}>30 днів</button>
      <button className="btn" onClick={() => preset(365)}>рік</button>
    </div>
  );
}

// Дані екрана: один хук на всі, щоб кожен не переписував стан і помилку.
export function useData(load, deps = []) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const fn = useRef(load);
  fn.current = load;
  const reload = useMemo(() => async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      setState({ loading: false, data: await fn.current(), error: null });
    } catch (e) {
      setState({ loading: false, data: null, error: e });
    }
  }, []);
  useEffect(() => { reload(); }, deps);
  return { ...state, reload };
}

// Номер сторінки списку (власник, 16.10.2026: n-на сторінка, а не
// підвантаження). Скидається на першу, щойно змінився будь-який фільтр із
// resetOn: інакше «сторінка 7» нового фільтра могла б виявитись порожньою.
// Скидаємо просто під час рендеру (так React радить для похідного стану):
// запит із фільтром і старою сторінкою не встигає піти.
export function usePage(...resetOn) {
  const [page, setPage] = useState(1);
  const key = JSON.stringify(resetOn);
  const seen = useRef(key);
  if (seen.current !== key) {
    seen.current = key;
    if (page !== 1) setPage(1);
  }
  return [page, setPage];
}

// Перемикач сторінок під таблицею: «51–100 з 734» і кнопки. info — те, що
// віддає api (paging.js: page, per, total). Після переходу повертаємо екран
// до початку таблиці, якщо він уже під верхнім краєм: інакше нова сторінка
// відкривалась би з кінця, там, де стояв перемикач.
//
// Номери — десять найближчих сторінок і завжди перша й остання (власник,
// 18.10.2026: відео з сотнями сторінок «вперед-назад» не перегорнеш). На
// телефоні лишаються сусіди на дві в кожен бік (.pager-far ховає решту), а
// для стрибка далеко — поле «до сторінки».
const PAGER_WINDOW = 10;
export function pagerWindow(page, pages, size = PAGER_WINDOW) {
  const start = Math.max(1, Math.min(page - Math.floor((size - 1) / 2), pages - size + 1));
  const end = Math.min(pages, start + size - 1);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

export function Pager({ info, onPage, busy = false, note = null }) {
  const ref = useRef(null);
  const [jump, setJump] = useState("");
  if (!info) return note;
  const pages = Math.max(1, Math.ceil(info.total / info.per));
  const from = info.total ? (info.page - 1) * info.per + 1 : 0;
  const to = Math.min(info.total, info.page * info.per);
  const goTo = (n) => {
    const next = Math.min(pages, Math.max(1, n));
    if (next !== info.page) onPage(next);
    const top = ref.current?.closest(".table-card, .threads, .card");
    if (top && top.getBoundingClientRect().top < 0) top.scrollIntoView({ block: "start" });
  };
  // Перша, остання й вікно; «…» — де номери не йдуть підряд. На телефоні
  // далекі номери сховані, тож там, де сховане не прикрите звичайною «…»,
  // стоїть своя, видима лише на телефоні (.pager-gap-m).
  const shown = [...new Set([1, ...pagerWindow(info.page, pages), pages])].sort((a, b) => a - b);
  const far = (n) => Math.abs(n - info.page) > 2 && n !== 1 && n !== pages;
  const items = [];
  shown.forEach((n, i) => {
    if (i > 0 && n - shown[i - 1] > 1) items.push(<span key={`g${n}`} className="pager-gap">…</span>);
    items.push(
      <button key={n} className={`btn small pager-num${n === info.page ? " on" : ""}${far(n) ? " pager-far" : ""}`}
              disabled={busy} aria-current={n === info.page ? "page" : undefined} onClick={() => goTo(n)}>
        {n}
      </button>
    );
    const next = far(n) ? undefined : shown.slice(i + 1).find((m) => !far(m));
    const run = next === undefined ? [] : shown.filter((m) => m >= n && m <= next);
    if (run.length > 2 && run.every((m, j) => j === 0 || m - run[j - 1] === 1)) {
      items.push(<span key={`m${n}`} className="pager-gap pager-gap-m">…</span>);
    }
  });
  const submit = (e) => {
    e.preventDefault();
    const n = parseInt(jump, 10);
    if (Number.isFinite(n)) goTo(n);
    setJump("");
  };
  return (
    <div className="pager" ref={ref}>
      <span className="pager-info">
        {info.total ? `${fmt.int(from)}–${fmt.int(to)} з ${fmt.int(info.total)}` : "нічого"}
        {note && <span className="muted"> · {note}</span>}
      </span>
      {pages > 1 && (
        <span className="pager-btns">
          <button className="btn small" disabled={busy || info.page <= 1} onClick={() => goTo(info.page - 1)} aria-label="попередня сторінка">‹</button>
          {items}
          <button className="btn small" disabled={busy || info.page >= pages} onClick={() => goTo(info.page + 1)} aria-label="наступна сторінка">›</button>
          {pages > PAGER_WINDOW && (
            <form className="pager-jump" onSubmit={submit}>
              <input type="number" min="1" max={pages} inputMode="numeric" value={jump} placeholder="№"
                     aria-label={`до сторінки, 1–${pages}`} onChange={(e) => setJump(e.target.value)} />
              <button className="btn small" disabled={busy || !jump}>→</button>
            </form>
          )}
        </span>
      )}
    </div>
  );
}

// foot — рядок під таблицею, у тій самій картці: там живуть підказки про
// незбережений стан і перемикач сторінок, які мусять бути притулені до
// рядків, а не висіти окремим блоком десь поруч.
// scroll — таблиця ширша за картку (кожна метрика — своя колонка): гортати
// вбік усередині картки, а не розсувати сторінку. На телефоні звичайна
// таблиця стає стосом карток «підпис — значення» (theme.css, data-label), а
// scroll лишається таблицею: двадцять метрик у картці — це екран прокрутки.
export const Table = ({ columns, rows, onRow, empty = "порожньо", foot = null, scroll = false }) => (
  <div className={`table-card${scroll ? " wide" : ""}`}>
    <div className="table-scroll">
    <table>
      <thead>
        <tr>{columns.map((c) => <th key={c.key} className={c.num ? "num" : ""} style={c.width ? { width: c.width } : undefined}>{c.title}</th>)}</tr>
      </thead>
      <tbody>
        {rows.length === 0 && <tr className="empty-row"><td colSpan={columns.length}><div className="empty">{empty}</div></td></tr>}
        {rows.map((r, i) => (
          <tr key={r.id ?? i} data-click={onRow ? "1" : undefined} onClick={onRow ? () => onRow(r) : undefined}>
            {columns.map((c) => (
              <td key={c.key} className={c.num ? "num" : ""} data-label={typeof c.title === "string" ? c.title : ""}>
                {c.render ? c.render(r) : r[c.key]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
    </div>
    {foot && <div className="table-foot">{foot}</div>}
  </div>
);
